var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { LeagueEvent } from '~/enum/event';
import { EventWS, WSDataFiled, WSDataTypeValue } from '~/enum/ws';
import { isMobileDevice } from '~/utils/common';
import { logger } from './Logger';
const MAX_MISSING_PONG = 1;
class WSocket {
    constructor(url, options) {
        this.pingNum = 0;
        this.pongNum = 0;
        this.url = url;
        this.options = {
            reconnectInterval: (options === null || options === void 0 ? void 0 : options.reconnectInterval) || 5000,
            maxReconnectAttempts: (options === null || options === void 0 ? void 0 : options.maxReconnectAttempts) || 5,
            heartbeatInterval: (options === null || options === void 0 ? void 0 : options.heartbeatInterval) || 5000,
            debug: (options === null || options === void 0 ? void 0 : options.debug) || false,
        };
        this.ws = null;
        this.reconnectCount = 0;
        this.isConnecting = false;
        this.messageQueue = [];
        // Bind methods
        this.connect = this.connect.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
        // Initialize visibility listeners
        this.setupVisibilityListener();
        // Initialize offline/online listeners
        this.setupConnectivityListeners();
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.isConnecting || (this.ws && this.ws.readyState === WebSocket.OPEN)) {
                return;
            }
            this.isConnecting = true;
            try {
                this.ws = new WebSocket(this.url);
                this.ws.onopen = () => {
                    this.log('WebSocket connected');
                    this.isConnecting = false;
                    this.reconnectCount = 0;
                    this.setupHeartbeat();
                    this.flushMessageQueue();
                };
                this.ws.onclose = () => {
                    this.log('WebSocket closed');
                    this.isConnecting = false;
                };
                this.ws.onerror = error => {
                    this.log('WebSocket error:', error);
                    this.isConnecting = false;
                    this.handleReconnection();
                };
                this.ws.onmessage = event => {
                    this.handleMessage(event);
                };
            }
            catch (error) {
                this.log('WebSocket connection error:', error);
                this.isConnecting = false;
                this.handleReconnection();
            }
        });
    }
    getReadyState() {
        var _a;
        return (_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState;
    }
    handleReconnection() {
        if (this.reconnectCount < this.options.maxReconnectAttempts) {
            this.reconnectCount++;
            const delay = this.calculateReconnectDelay();
            this.log(`Attempting to reconnect in ${delay}ms (attempt ${this.reconnectCount})`);
            setTimeout(() => {
                if (!document.hidden) {
                    this.connect();
                }
            }, delay);
        }
    }
    setupVisibilityListener() {
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }
    setupConnectivityListeners() {
        window.addEventListener('online', () => {
            this.log('Network is online');
            // if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            //   this.connect();
            // }
            this.handleReconnectionToComponent();
        });
        window.addEventListener('offline', () => {
            this.log('Network is offline');
            this.cleanup();
            this.handleCloseConnection();
        });
    }
    handleReconnectionToComponent() {
        return __awaiter(this, void 0, void 0, function* () {
            window.dispatchEvent(new CustomEvent(EventWS.webSocketEventListReconnect, { detail: { reconnect: true } }));
        });
    }
    handleCloseConnection() {
        return __awaiter(this, void 0, void 0, function* () {
            this.log('WebSocket closed - Cleaning up');
            this.cleanup();
            this.reconnectCount = 0;
            this.isConnecting = false;
            this.pingNum = 0;
            this.pongNum = 0;
            this.messageQueue = [];
            this.ws = null;
            this.heartbeatInterval = null;
            this.log('WebSocket closed - Setting up ping interval');
            window.dispatchEvent(new CustomEvent(EventWS.webSocketEventListReconnect, { detail: { reconnect: false } }));
        });
    }
    handleVisibilityChange() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            const isMobile = isMobileDevice();
            if (document.hidden) {
                // Page is hidden
                this.log('Page hidden - Setting up ping interval');
                // this.setupHeartbeat();
                isMobile && this.handleCloseConnection();
            }
            else {
                // Page is visible
                this.log('Page visible - Checking connection', (_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState);
                yield this.handleReconnectionToComponent();
                if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
                    this.log('Reconnecting to WebSocket background');
                    yield this.connect();
                }
                // if (this.options.type === TypeSocket.EventList) {
                //   this.log('Reconnecting to WebSocket background');
                //   await this.connect();
                // }
            }
        });
    }
    setupHeartbeat() {
        // Clear existing interval if any
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }
        // Set up new heartbeat interval
        this.heartbeatInterval = setInterval(() => {
            // Check missing pong
            if (this.pingNum - this.pongNum > MAX_MISSING_PONG) {
                this.pingNum = 0;
                this.pongNum = 0;
                this.cleanup();
                return;
            }
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.pingNum++;
                this.ws.send(`ping_${this.pingNum}`);
            }
        }, this.options.heartbeatInterval);
    }
    cleanup() {
        if (this.heartbeatInterval) {
            clearInterval(this.heartbeatInterval);
        }
        if (this.ws) {
            this.log('Closing WebSocket connection');
            this.ws.close();
        }
    }
    log(...args) {
        if (this.options.debug) {
            logger.info('[WebSocketManager]', ...args);
        }
    }
    calculateReconnectDelay() {
        // Exponential backoff with jitter
        const baseDelay = this.options.reconnectInterval;
        const exponentialDelay = baseDelay * Math.pow(2, this.reconnectCount - 1);
        const jitter = Math.random() * 1000; // Add up to 1 second of random jitter
        return Math.min(exponentialDelay + jitter, 30000); // Cap at 30 seconds
    }
    send(data) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        }
        else {
            this.messageQueue.push(data);
            this.connect();
        }
    }
    flushMessageQueue() {
        var _a;
        while (this.messageQueue.length > 0 && ((_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState) === WebSocket.OPEN) {
            const data = this.messageQueue.shift();
            this.send(data);
        }
    }
    handleMessage(event) {
        var _a, _b, _c;
        try {
            const data = (_a = JSON.parse(event.data)) !== null && _a !== void 0 ? _a : {};
            if (data.hasOwnProperty('pong')) {
                this.pongNum = data.pong;
                return;
            }
            // Handle different message types
            if ((data === null || data === void 0 ? void 0 : data[WSDataFiled.TYPE]) === WSDataTypeValue.Current) {
                const eventListCurrent = JSON.parse(data === null || data === void 0 ? void 0 : data[WSDataFiled.DATA]);
                const firstMatch = (_c = (_b = eventListCurrent === null || eventListCurrent === void 0 ? void 0 : eventListCurrent[0]) === null || _b === void 0 ? void 0 : _b[LeagueEvent.EventList]) === null || _c === void 0 ? void 0 : _c[0];
                if (!firstMatch) {
                    window.dispatchEvent(new CustomEvent(EventWS.webSocketEventListNotMatch, { detail: { isNotMatch: true } }));
                    return;
                }
                else {
                    window.dispatchEvent(new CustomEvent(EventWS.webSocketEventList, { detail: firstMatch }));
                }
            }
        }
        catch (error) {
            this.log('Error handling message:', error);
        }
    }
}
export default WSocket;
